window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  var EventBus = JRS.EventBus;
  var ConfigurationState = JRS.ConfigurationState;
  var PRODUCT_SELECTOR_STRATEGIES = JRS.PRODUCT_SELECTOR_STRATEGIES;

  var _productCache = {};
  var _activeStrategy = null;
  var _boundListeners = [];

  var _singleState = null;
  var _msState = null;
  var _bundleState = null;

  function init(strategyName, config) {
    destroy();

    var name = String(strategyName || '');
    var strategy = _getStrategyByName(name);
    if (!strategy) {
      throw new Error('ProductSelector: Unknown strategy "' + name + '"');
    }

    strategy.init(config || {});
    _activeStrategy = { name: name, strategy: strategy };
  }

  function destroy() {
    if (_activeStrategy && _activeStrategy.strategy && typeof _activeStrategy.strategy.destroy === 'function') {
      try {
        _activeStrategy.strategy.destroy();
      } catch (err) {
        console.error('[ProductSelector] Strategy destroy failed', err);
      }
    }

    for (var i = 0; i < _boundListeners.length; i++) {
      var item = _boundListeners[i];
      if (!item || !item.el || !item.event || !item.handler) continue;
      item.el.removeEventListener(item.event, item.handler);
    }
    _boundListeners = [];

    _activeStrategy = null;
    _singleState = null;
    _msState = null;
    _bundleState = null;
  }

  function getState() {
    var snapshot = {
      activeStrategy: _activeStrategy ? _activeStrategy.name : null,
      state: null
    };

    if (!_activeStrategy) return snapshot;

    if (_activeStrategy.name === PRODUCT_SELECTOR_STRATEGIES.SINGLE_VARIANT) {
      snapshot.state = _safeClone(_singleState);
    } else if (_activeStrategy.name === PRODUCT_SELECTOR_STRATEGIES.MULTI_STEP) {
      snapshot.state = _safeClone(_msState);
    } else if (_activeStrategy.name === PRODUCT_SELECTOR_STRATEGIES.BUNDLE_CATEGORY) {
      snapshot.state = _safeClone(_bundleState);
    }

    return snapshot;
  }

  var singleVariantStrategy = {
    init: function(cfg) {
      var sectionEl = cfg.sectionEl || document;
      var productJSON = cfg.productJSON || _readProductJSON(sectionEl);
      if (!productJSON) {
        throw new Error('ProductSelector.singleVariant: Missing productJSON');
      }

      _singleState = {
        sectionEl: sectionEl,
        productJSON: productJSON,
        selectedVariantId: null,
        selectedColour: null,
        selectedSizes: []
      };

      ConfigurationState.init('standard');
      ConfigurationState.setCategory(0, String(productJSON.title || 'Product'));

      var initialVariant = _getInitialVariant(productJSON, sectionEl);
      if (initialVariant) {
        _singleState.selectedVariantId = Number(initialVariant.id || 0);
        _singleState.selectedColour = String(initialVariant.option1 || '');
        _setStandardProductFromVariant(productJSON, initialVariant);
        _updatePriceDisplay(sectionEl, initialVariant);
      }

      _bindSingleVariantEvents(cfg || {});
    },
    destroy: function() {
      if (_singleState) {
        _singleState.sectionEl = null;
        _singleState.productJSON = null;
      }
    }
  };

  var multiStepStrategy = {
    init: function(cfg) {
      var sectionEl = cfg.sectionEl || document;
      var productJSON = cfg.productJSON || _readProductJSON(sectionEl);
      var mount = _findMultiStepMount(sectionEl);
      if (!productJSON) throw new Error('ProductSelector.multiStep: Missing productJSON');
      if (!mount) {
        console.warn('ProductSelector.multiStep: Missing multistep mount selector; skipping multi-step init');
        _msState = null;
        return;
      }
      if (_isV2MultiStepOwnedMount(mount)) {
        console.warn('ProductSelector.multiStep: v2 multistep mount owner detected; skipping legacy jrs-ms renderer');
        _msState = null;
        return;
      }

      _msState = {
        sectionEl: sectionEl,
        productJSON: productJSON,
        mount: mount,
        isCustomized: !!cfg.isCustomized,
        currentStep: 1,
        selectedColour: null,
        quantities: {},
        totalQty: 0
      };

      ConfigurationState.init('standard');
      ConfigurationState.setCategory(0, String(productJSON.title || 'Product'));

      _renderMultiStepStep1();
      _bindMultiStepEvents();
    },
    destroy: function() {
      if (_msState && _msState.mount && !_isV2MultiStepOwnedMount(_msState.mount)) {
        _msState.mount.innerHTML = '';
      }
    }
  };

  var bundleCategoryStrategy = {
    init: function(cfg) {
      var sectionEl = cfg.sectionEl || document;
      var configurator = sectionEl.querySelector('[data-jrs-bundle-configurator]') || sectionEl;
      var categoryEls = configurator.querySelectorAll('[data-jrs-category-index]');

      _bundleState = {
        sectionEl: sectionEl,
        configurator: configurator,
        categories: {},
        productModels: {},
        emittedCategoryComplete: {},
        totalCategories: categoryEls.length
      };

      ConfigurationState.init('bundle', {
        metaobjectId: cfg.bundleMetaobjectId || '',
        productHandle: cfg.bundleProductHandle || ''
      });

      for (var i = 0; i < categoryEls.length; i++) {
        var catEl = categoryEls[i];
        var index = Number(catEl.getAttribute('data-jrs-category-index') || i);
        var name = String(catEl.getAttribute('data-category-name') || 'Category ' + (index + 1));
        var limit = Number(catEl.getAttribute('data-category-limit') || 0);

        ConfigurationState.setCategory(index, name);
        _bundleState.categories[index] = {
          index: index,
          name: name,
          limit: limit
        };
      }

      _bindBundleEvents();
      _expandFirstBundleCategory();
    },
    destroy: function() {}
  };

  function _getStrategyByName(name) {
    if (name === PRODUCT_SELECTOR_STRATEGIES.SINGLE_VARIANT) return singleVariantStrategy;
    if (name === PRODUCT_SELECTOR_STRATEGIES.MULTI_STEP) return multiStepStrategy;
    if (name === PRODUCT_SELECTOR_STRATEGIES.BUNDLE_CATEGORY) return bundleCategoryStrategy;
    return null;
  }

  function _fetchProductJSON(handle) {
    var h = String(handle || '');
    if (!h) {
      return Promise.reject(new Error('ProductSelector: Missing product handle'));
    }
    if (_productCache[h]) {
      return Promise.resolve(_productCache[h]);
    }

    return fetch('/products/' + h + '.js')
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status + ' loading product ' + h);
        return response.json();
      })
      .then(function(data) {
        _productCache[h] = data;
        return data;
      });
  }

  function _buildSizeGrid(container, variants, config) {
    if (!container) return;
    var list = variants || [];
    var cfg = config || {};
    container._jrsSizeGridConfig = cfg;
    var html = '';

    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      var size = _variantSize(v);
      var available = v && v.available !== false;
      var max = Number(cfg.maxQty || ((typeof v.inventory_quantity === 'number' && v.inventory_quantity > 0) ? v.inventory_quantity : 999));
      if (max < 1) max = 1;

      html += '<div class=\"jrs-size-row\"';
      html += ' data-variant-id=\"' + String(v.id || '') + '\"';
      html += ' data-size=\"' + _escapeAttr(size) + '\"';
      html += ' data-sku=\"' + _escapeAttr(String(v.sku || '')) + '\"';
      html += ' data-category-index=\"' + _escapeAttr(String(cfg.categoryIndex || '')) + '\"';
      html += ' data-product-index=\"' + _escapeAttr(String(cfg.productIndex || '')) + '\">';
      html += '<div class=\"jrs-size-label\">' + _escapeHTML(size) + '</div>';
      if (cfg.showStock) {
        html += '<div class=\"jrs-stock\">' + String(v.inventory_quantity || 0) + '</div>';
      }
      html += '<div class=\"jrs-qty-stepper\">';
      html += '<button type=\"button\" data-action=\"decrease\"' + (available ? '' : ' disabled') + '>-</button>';
      html += '<input type=\"number\" class=\"jrs-qty-input\" aria-label=\"Quantity ' + _escapeAttr(String(size)) + '\" min=\"0\" max=\"' + String(max) + '\" value=\"0\"' + (available ? '' : ' disabled') + '>';
      html += '<button type=\"button\" data-action=\"increase\"' + (available ? '' : ' disabled') + '>+</button>';
      html += '</div>';
      html += '</div>';
    }

    container.innerHTML = html;

    if (container.getAttribute('data-jrs-size-grid-bound') === 'true') {
      return;
    }
    container.setAttribute('data-jrs-size-grid-bound', 'true');

    _bindListener(container, 'click', function(e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-action]') : null;
      if (!btn) return;
      var row = btn.closest('.jrs-size-row');
      if (!row) return;
      var input = row.querySelector('.jrs-qty-input');
      if (!input || input.disabled) return;

      var current = Number(input.value || 0);
      var max = Number(input.getAttribute('max') || 999);
      var action = btn.getAttribute('data-action');

      if (action === 'increase' && current < max) {
        input.value = String(current + 1);
      } else if (action === 'decrease' && current > 0) {
        input.value = String(current - 1);
      }

      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    _bindListener(container, 'input', function(e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('jrs-qty-input')) return;
      var row = input.closest('.jrs-size-row');
      if (!row) return;
      var liveCfg = container._jrsSizeGridConfig || {};

      var max = Number(input.getAttribute('max') || 999);
      var qty = Number(input.value || 0);
      if (qty < 0) qty = 0;
      if (qty > max) qty = max;
      input.value = String(qty);

      if (typeof liveCfg.onQtyChange === 'function') {
        liveCfg.onQtyChange(
          String(row.getAttribute('data-size') || ''),
          qty,
          Number(row.getAttribute('data-variant-id') || 0),
          String(row.getAttribute('data-sku') || '')
        );
      }
    });
  }

  function _formatPrice(priceInCents) {
    var cents = Number(priceInCents || 0);
    var pounds = cents / 100;
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pounds);
      } catch (e) {
        void e;
      }
    }
    return '£' + pounds.toFixed(2);
  }

  function _getVariantsForColour(productData, colour) {
    var all = (productData && productData.variants) ? productData.variants : [];
    var target = String(colour || '').toLowerCase();
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var variantColour = String(all[i].option1 || all[i].color || '').toLowerCase();
      if (!target || variantColour === target) {
        out.push(all[i]);
      }
    }
    return out;
  }

  function _bindListener(el, event, handler) {
    if (!el || !event || !handler) return;
    el.addEventListener(event, handler);
    _boundListeners.push({ el: el, event: event, handler: handler });
  }

  function _bindSingleVariantEvents(cfg) {
    var sectionEl = _singleState.sectionEl;
    var productJSON = _singleState.productJSON;

    var swatchList = sectionEl.querySelector('[data-jrs-swatchlist]');
    if (swatchList) {
      _bindListener(swatchList, 'click', function(e) {
        var swatch = e.target && e.target.closest ? e.target.closest('[data-jrs-swatch]') : null;
        if (!swatch) return;

        var colour = String(swatch.getAttribute('data-colour') || '');
        var variantId = Number(swatch.getAttribute('data-variant-id') || 0);
        var variant = _findVariantById(productJSON, variantId);
        if (!variant) {
          variant = _firstVariantForColour(productJSON, colour);
        }
        if (!variant) return;

        _setActiveInGroup(swatchList.querySelectorAll('[data-jrs-swatch]'), swatch, 'is-active');
        _singleState.selectedColour = colour || String(variant.option1 || '');
        _singleState.selectedVariantId = Number(variant.id || 0);

        _setStandardProductFromVariant(productJSON, variant);
        _updatePriceDisplay(sectionEl, variant);

        EventBus.emit('product:colourSelected', {
          categoryIndex: 0,
          colour: _singleState.selectedColour,
          productHandle: String(productJSON.handle || '')
        });
      });
    }

    var variantSelect = sectionEl.querySelector('[data-jrs-product-select]');
    if (variantSelect) {
      _bindListener(variantSelect, 'change', function() {
        var variantId = Number(variantSelect.value || 0);
        var variant = _findVariantById(productJSON, variantId);
        if (!variant) return;

        _singleState.selectedVariantId = Number(variant.id || 0);
        _singleState.selectedColour = String(variant.option1 || '');

        _setStandardProductFromVariant(productJSON, variant);
        _updatePriceDisplay(sectionEl, variant);

        EventBus.emit('product:colourSelected', {
          categoryIndex: 0,
          colour: _singleState.selectedColour,
          productHandle: String(productJSON.handle || '')
        });
      });
    }

    var sizeGrid = sectionEl.querySelector('[data-jrs-size-grid]');
    if (sizeGrid) {
      _bindListener(sizeGrid, 'click', function(e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-jrs-size-btn]') : null;
        if (!btn) return;
        if (String(btn.getAttribute('data-available') || 'true') === 'false') return;

        var variantId = Number(btn.getAttribute('data-variant-id') || 0);
        var size = String(btn.getAttribute('data-size') || '');
        var variant = _findVariantById(productJSON, variantId);
        var sku = variant ? String(variant.sku || '') : '';

        _setActiveInGroup(sizeGrid.querySelectorAll('[data-jrs-size-btn]'), btn, 'is-active');

        var sizes = [{ size: size, quantity: 1, variantId: variantId, sku: sku }];
        _singleState.selectedSizes = sizes;
        ConfigurationState.setSizes(0, 0, sizes);

        EventBus.emit('product:sizesChanged', {
          categoryIndex: 0,
          sizes: sizes,
          totalQuantity: 1
        });
      });
    }

    var multiSizeGrid = sectionEl.querySelector('[data-jrs-multi-size-grid]');
    if (multiSizeGrid) {
      _bindListener(multiSizeGrid, 'input', function(e) {
        var input = e.target;
        if (!input || !input.classList || !input.classList.contains('jrs-qty-input')) return;

        var rows = multiSizeGrid.querySelectorAll('.jrs-size-row');
        var sizes = [];
        var total = 0;

        for (var i = 0; i < rows.length; i++) {
          var row = rows[i];
          var qtyInput = row.querySelector('.jrs-qty-input');
          if (!qtyInput) continue;
          var qty = Number(qtyInput.value || 0);
          if (qty <= 0) continue;

          var variantId = Number(row.getAttribute('data-variant-id') || 0);
          var size = String(row.getAttribute('data-size') || '');
          var variant = _findVariantById(productJSON, variantId);
          var sku = variant ? String(variant.sku || '') : String(row.getAttribute('data-sku') || '');

          sizes.push({ size: size, quantity: qty, variantId: variantId, sku: sku });
          total += qty;
        }

        _singleState.selectedSizes = sizes;
        if (sizes.length > 0) {
          ConfigurationState.setSizes(0, 0, sizes);
        }

        EventBus.emit('product:sizesChanged', {
          categoryIndex: 0,
          sizes: sizes,
          totalQuantity: total
        });
      });
    }

    if (cfg && cfg.hasMultipleSizes && !multiSizeGrid) {
      console.warn('[ProductSelector] hasMultipleSizes=true but [data-jrs-multi-size-grid] not found');
    }
  }

  function _bindMultiStepEvents() {
    var mount = _msState.mount;

    _bindListener(mount, 'click', function(e) {
      var pill = e.target && e.target.closest ? e.target.closest('.jrs-colour-pill') : null;
      if (pill) {
        _onMultiStepColourSelected(pill);
        return;
      }

      var actionEl = e.target && e.target.closest ? e.target.closest('[data-action]') : null;
      if (!actionEl) return;

      var action = actionEl.getAttribute('data-action');
      if (action === 'continue-step2') {
        _goMultiStep2();
      } else if (action === 'back-step1') {
        _goMultiStep1();
      }
    });
  }

  function _deriveColours(variants) {
    var list = variants || [];
    var seen = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var colour = String(list[i].option1 || '');
      if (!colour || seen[colour]) continue;
      seen[colour] = true;
      out.push({
        colour: colour,
        representativeVariantId: Number(list[i].id || 0)
      });
    }
    return out;
  }

  function _renderMultiStepStep1() {
    var colours = _deriveColours(_msState.productJSON.variants || []);
    var html = '';
    html += '<div class=\"jrs-ms-step\" data-step=\"1\">';
    html += '<div class=\"jrs-ms-colour-grid\">';

    for (var i = 0; i < colours.length; i++) {
      var isActive = _msState.selectedColour && _msState.selectedColour === colours[i].colour;
      html += '<button type=\"button\" class=\"jrs-colour-pill' + (isActive ? ' is-active' : '') + '\"';
      html += ' data-colour=\"' + _escapeAttr(colours[i].colour) + '\"';
      html += ' data-variant-id=\"' + String(colours[i].representativeVariantId) + '\">';
      html += _escapeHTML(colours[i].colour);
      html += '</button>';
    }

    html += '</div>';
    html += '<button type=\"button\" data-action=\"continue-step2\"' + (_msState.selectedColour ? '' : ' disabled') + '>Continue</button>';
    html += '</div>';

    _msState.mount.innerHTML = html;
    _msState.currentStep = 1;
  }

  function _onMultiStepColourSelected(pillEl) {
    var colour = String(pillEl.getAttribute('data-colour') || '');
    var variantId = Number(pillEl.getAttribute('data-variant-id') || 0);
    var variant = _findVariantById(_msState.productJSON, variantId) || _firstVariantForColour(_msState.productJSON, colour);

    _msState.selectedColour = colour;
    _setActiveInGroup(_msState.mount.querySelectorAll('.jrs-colour-pill'), pillEl, 'is-active');

    var continueBtn = _msState.mount.querySelector('[data-action=\"continue-step2\"]');
    if (continueBtn) continueBtn.disabled = !colour;

    if (variant) {
      _setStandardProductFromVariant(_msState.productJSON, variant);
    }

    EventBus.emit('product:colourSelected', {
      categoryIndex: 0,
      colour: colour,
      productHandle: String(_msState.productJSON.handle || '')
    });
  }

  function _goMultiStep2() {
    if (!_msState.selectedColour) return;
    var variants = _getVariantsForColour(_msState.productJSON, _msState.selectedColour);

    var html = '';
    html += '<div class=\"jrs-ms-step\" data-step=\"2\">';
    html += '<div class=\"jrs-ms-size-grid\" data-jrs-ms-size-grid></div>';
    html += '<div class=\"jrs-ms-total\">Total: <span data-jrs-ms-total>0</span></div>';
    html += '<button type=\"button\" data-action=\"back-step1\">Back</button>';
    html += '<button type=\"button\" data-jrs-ms-atc disabled>Add to cart</button>';
    html += '</div>';
    _msState.mount.innerHTML = html;
    _msState.currentStep = 2;

    var grid = _msState.mount.querySelector('[data-jrs-ms-size-grid]');
    _buildSizeGrid(grid, variants, {
      showStock: true,
      onQtyChange: function(size, qty, variantId, sku) {
        _msState.quantities[String(variantId)] = qty;
        _recomputeMultiStepSizes(variants, size, qty, variantId, sku);
      }
    });

    EventBus.emit('pdp:stepChanged', {
      step: 2,
      maxSteps: _msState.isCustomized ? 3 : 2
    });
  }

  function _recomputeMultiStepSizes(variants) {
    var list = variants || [];
    var sizes = [];
    var total = 0;

    for (var i = 0; i < list.length; i++) {
      var variant = list[i];
      var qty = Number(_msState.quantities[String(variant.id)] || 0);
      if (qty <= 0) continue;
      sizes.push({
        size: _variantSize(variant),
        quantity: qty,
        variantId: Number(variant.id || 0),
        sku: String(variant.sku || '')
      });
      total += qty;
    }

    _msState.totalQty = total;
    if (sizes.length > 0) {
      ConfigurationState.setSizes(0, 0, sizes);
    }

    var totalEl = _msState.mount.querySelector('[data-jrs-ms-total]');
    if (totalEl) totalEl.textContent = String(total);
    var atc = _msState.mount.querySelector('[data-jrs-ms-atc]');
    if (atc) atc.disabled = total <= 0;

    EventBus.emit('product:sizesChanged', {
      categoryIndex: 0,
      sizes: sizes,
      totalQuantity: total
    });

    if (typeof window.cvdUpdateProgress === 'function') {
      window.cvdUpdateProgress(total);
    }
  }

  function _goMultiStep1() {
    _renderMultiStepStep1();
    EventBus.emit('pdp:stepChanged', {
      step: 1,
      maxSteps: _msState.isCustomized ? 3 : 2
    });
  }

  function _bindBundleEvents() {
    var root = _bundleState.configurator;

    _bindListener(root, 'click', function(e) {
      var toggle = e.target && e.target.closest ? e.target.closest('[data-jrs-category-toggle]') : null;
      if (toggle) {
        var section = toggle.closest('[data-jrs-category-index]');
        if (section) _toggleBundleCategory(section);
      }
    });

    _bindListener(root, 'change', function(e) {
      var dropdown = e.target;
      if (!dropdown || !dropdown.classList || !dropdown.classList.contains('jrs-colour-dropdown')) return;
      _onBundleColourSelected(dropdown);
    });
  }

  function _expandFirstBundleCategory() {
    var categories = _bundleState.configurator.querySelectorAll('[data-jrs-category-index]');
    if (!categories || categories.length === 0) return;
    _setBundleCategoryExpanded(categories[0], true);
  }

  function _toggleBundleCategory(section) {
    var expanded = section.getAttribute('data-jrs-expanded') === 'true';
    _setBundleCategoryExpanded(section, !expanded);
  }

  function _setBundleCategoryExpanded(section, expanded) {
    section.setAttribute('data-jrs-expanded', expanded ? 'true' : 'false');
    var body = section.querySelector('.jrs-category-body');
    if (!body) return;
    body.style.display = expanded ? 'block' : 'none';
  }

  function _onBundleColourSelected(dropdown) {
    var card = dropdown.closest('.jrs-bundle-product-card');
    var categorySection = dropdown.closest('[data-jrs-category-index]');
    if (!card || !categorySection) return;

    var categoryIndex = Number(categorySection.getAttribute('data-jrs-category-index') || 0);
    var productIndex = _bundleProductIndex(categorySection, card);
    var handle = String(card.getAttribute('data-product-handle') || dropdown.getAttribute('data-product-handle') || '');
    var colour = String(dropdown.value || dropdown.getAttribute('data-colour') || '');
    var productId = Number(card.getAttribute('data-product-id') || 0);
    var productTitle = String(card.getAttribute('data-product-title') || '');

    if (!handle || !colour) return;

    var seq = Number(card.getAttribute('data-jrs-fetch-seq') || 0) + 1;
    card.setAttribute('data-jrs-fetch-seq', String(seq));

    _fetchProductJSON(handle).then(function(productData) {
      // Stale fetch guard: sequence + currently selected colour must still match.
      var liveSeq = Number(card.getAttribute('data-jrs-fetch-seq') || 0);
      var liveColour = String(dropdown.value || dropdown.getAttribute('data-colour') || '');
      if (liveSeq !== seq || liveColour !== colour) {
        return;
      }

      var variants = _getVariantsForColour(productData, colour);
      var container = card.querySelector('[data-jrs-size-container]');

      _buildSizeGrid(container, variants, {
        categoryIndex: categoryIndex,
        productIndex: productIndex,
        showStock: false,
        onQtyChange: function(size, qty, variantId, sku) {
          _onBundleSizeQtyChange(categoryIndex, productIndex, card, size, qty, variantId, sku);
        }
      });

      var firstVariant = variants.length > 0 ? variants[0] : null;
      var imageUrl = '';
      if (firstVariant && firstVariant.featured_image && firstVariant.featured_image.src) {
        imageUrl = String(firstVariant.featured_image.src || '');
      } else if (productData && productData.images && productData.images.length > 0) {
        imageUrl = String(productData.images[0] || '');
      }

      var model = {
        productId: Number(productId || productData.id || 0),
        productHandle: handle,
        productTitle: productTitle || String(productData.title || ''),
        variantId: Number(firstVariant ? firstVariant.id : 0),
        colour: colour,
        imageUrl: imageUrl || null,
        sizes: []
      };

      _setBundleProductModel(categoryIndex, productIndex, model);
      ConfigurationState.setProduct(categoryIndex, productIndex, model);

      EventBus.emit('product:colourSelected', {
        categoryIndex: categoryIndex,
        colour: colour,
        productHandle: handle
      });
    }).catch(function(err) {
      console.error('[ProductSelector] Bundle colour fetch failed', err);
    });
  }

  function _onBundleSizeQtyChange(categoryIndex, productIndex, card, size, qty, variantId, sku) {
    void size;
    void qty;
    void variantId;
    void sku;

    var rows = card.querySelectorAll('.jrs-size-row');
    var sizes = [];
    var i;

    for (i = 0; i < rows.length; i++) {
      var row = rows[i];
      var input = row.querySelector('.jrs-qty-input');
      if (!input) continue;
      var q = Number(input.value || 0);
      if (q <= 0) continue;

      sizes.push({
        size: String(row.getAttribute('data-size') || ''),
        quantity: q,
        variantId: Number(row.getAttribute('data-variant-id') || 0),
        sku: String(row.getAttribute('data-sku') || '')
      });
    }

    var model = _getBundleProductModel(categoryIndex, productIndex);
    if (model) {
      model.sizes = sizes;
      ConfigurationState.setProduct(categoryIndex, productIndex, model);
    }
    if (sizes.length > 0) {
      ConfigurationState.setSizes(categoryIndex, productIndex, sizes);
    }

    var categoryTotal = _bundleCategoryTotal(categoryIndex);
    EventBus.emit('product:sizesChanged', {
      categoryIndex: categoryIndex,
      sizes: sizes,
      totalQuantity: categoryTotal
    });

    _checkCategoryComplete(categoryIndex);
    _checkAllCategoriesComplete();
  }

  function _checkCategoryComplete(categoryIndex) {
    var category = _bundleState.categories[categoryIndex];
    if (!category) return;
    var total = _bundleCategoryTotal(categoryIndex);
    var complete = category.limit > 0 && total >= category.limit;
    var already = !!_bundleState.emittedCategoryComplete[categoryIndex];

    if (complete && !already) {
      _bundleState.emittedCategoryComplete[categoryIndex] = true;
      EventBus.emit('bundle:categoryCompleted', {
        categoryIndex: Number(categoryIndex),
        categoryName: String(category.name || '')
      });
      _expandNextIncompleteCategory(categoryIndex);
      return;
    }

    if (!complete) {
      _bundleState.emittedCategoryComplete[categoryIndex] = false;
    }
  }

  function _checkAllCategoriesComplete() {
    var keys = Object.keys(_bundleState.categories);
    if (keys.length === 0) return;

    var allComplete = true;
    var totalQuantity = 0;

    for (var i = 0; i < keys.length; i++) {
      var idx = Number(keys[i]);
      var category = _bundleState.categories[idx];
      var total = _bundleCategoryTotal(idx);
      totalQuantity += total;
      if (!(category.limit > 0 && total >= category.limit)) {
        allComplete = false;
      }
    }

    if (allComplete) {
      EventBus.emit('bundle:allCategoriesCompleted', {
        totalQuantity: totalQuantity,
        totalCategories: keys.length
      });
    }
  }

  function _bundleCategoryTotal(categoryIndex) {
    var modelsByCategory = _bundleState.productModels[String(categoryIndex)] || {};
    var productKeys = Object.keys(modelsByCategory);
    var total = 0;

    for (var i = 0; i < productKeys.length; i++) {
      var model = modelsByCategory[productKeys[i]];
      if (!model || !model.sizes) continue;
      for (var j = 0; j < model.sizes.length; j++) {
        total += Number(model.sizes[j].quantity || 0);
      }
    }
    return total;
  }

  function _expandNextIncompleteCategory(afterCategoryIndex) {
    var all = _bundleState.configurator.querySelectorAll('[data-jrs-category-index]');
    var start = Number(afterCategoryIndex) + 1;
    for (var i = 0; i < all.length; i++) {
      var section = all[i];
      var idx = Number(section.getAttribute('data-jrs-category-index') || 0);
      if (idx < start) continue;
      var category = _bundleState.categories[idx];
      if (!category) continue;
      if (_bundleCategoryTotal(idx) >= category.limit) continue;
      _setBundleCategoryExpanded(section, true);
      return;
    }
  }

  function _bundleProductIndex(categorySection, cardEl) {
    var cards = categorySection.querySelectorAll('.jrs-bundle-product-card');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i] === cardEl) return i;
    }
    return 0;
  }

  function _setBundleProductModel(categoryIndex, productIndex, model) {
    var catKey = String(categoryIndex);
    var prodKey = String(productIndex);
    if (!_bundleState.productModels[catKey]) _bundleState.productModels[catKey] = {};
    _bundleState.productModels[catKey][prodKey] = model;
  }

  function _getBundleProductModel(categoryIndex, productIndex) {
    var catKey = String(categoryIndex);
    var prodKey = String(productIndex);
    if (!_bundleState.productModels[catKey]) return null;
    return _bundleState.productModels[catKey][prodKey] || null;
  }

  function _setStandardProductFromVariant(productJSON, variant) {
    var imageUrl = _findVariantImage(productJSON, variant);
    var model = {
      productId: Number(productJSON.id || 0),
      productHandle: String(productJSON.handle || ''),
      productTitle: String(productJSON.title || ''),
      variantId: Number(variant.id || 0),
      colour: String(variant.option1 || ''),
      imageUrl: imageUrl || null,
      sizes: []
    };
    ConfigurationState.setProduct(0, 0, model);
  }

  function _findVariantImage(productJSON, variant) {
    if (variant && variant.featured_image && variant.featured_image.src) {
      return String(variant.featured_image.src || '');
    }
    if (variant && variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
      return String(variant.featured_media.preview_image.src || '');
    }
    if (productJSON && productJSON.images && productJSON.images.length > 0) {
      return String(productJSON.images[0] || '');
    }
    return '';
  }

  function _updatePriceDisplay(sectionEl, variant) {
    if (!sectionEl || !variant) return;
    var wrapper = sectionEl.querySelector('[data-jrs-price-wrapper]');
    if (!wrapper) return;

    var primary = wrapper.querySelector('[data-jrs-price-primary]');
    var secondary = wrapper.querySelector('[data-jrs-price-secondary]');
    var compare = wrapper.querySelector('[data-jrs-compare-price]');

    var price = Number(variant.price || 0);
    var comparePrice = Number(variant.compare_at_price || 0);

    if (primary) primary.textContent = _formatPrice(price);
    if (secondary) secondary.textContent = _formatPrice(Math.round(price * 1.2));
    if (compare) compare.textContent = comparePrice > price ? _formatPrice(comparePrice) : '';
  }

  function _getInitialVariant(productJSON, sectionEl) {
    var select = sectionEl.querySelector('[data-jrs-product-select]');
    if (select && select.value) {
      var bySelect = _findVariantById(productJSON, Number(select.value));
      if (bySelect) return bySelect;
    }
    if (productJSON && productJSON.selected_or_first_available_variant) {
      return productJSON.selected_or_first_available_variant;
    }
    if (productJSON && productJSON.variants && productJSON.variants.length > 0) {
      return productJSON.variants[0];
    }
    return null;
  }

  function _findVariantById(productJSON, variantId) {
    var variants = productJSON && productJSON.variants ? productJSON.variants : [];
    for (var i = 0; i < variants.length; i++) {
      if (Number(variants[i].id || 0) === Number(variantId || 0)) {
        return variants[i];
      }
    }
    return null;
  }

  function _firstVariantForColour(productJSON, colour) {
    var variants = _getVariantsForColour(productJSON, colour);
    return variants.length > 0 ? variants[0] : null;
  }

  function _variantSize(variant) {
    if (!variant) return '';
    if (typeof variant.option2 !== 'undefined' && variant.option2 !== null) return String(variant.option2);
    if (typeof variant.size !== 'undefined' && variant.size !== null) return String(variant.size);
    return '';
  }

  function _setActiveInGroup(nodeList, activeNode, className) {
    var cls = className || 'is-active';
    if (!nodeList) return;
    for (var i = 0; i < nodeList.length; i++) {
      if (!nodeList[i] || !nodeList[i].classList) continue;
      if (nodeList[i] === activeNode) {
        nodeList[i].classList.add(cls);
      } else {
        nodeList[i].classList.remove(cls);
      }
    }
  }

  function _readProductJSON(sectionEl) {
    var script = sectionEl.querySelector('[data-product-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (err) {
      console.error('[ProductSelector] Invalid product JSON', err);
      return null;
    }
  }

  /**
   * Support both old and new multistep mount contracts.
   *
   * @param {Element|Document} root
   * @returns {Element|null}
   */
  function _findMultiStepMount(root) {
    var scope = root || document;
    return scope.querySelector('[data-jrs-pdp-v2-mount], [data-pdp-v2-mount], [data-jrs-multistep-mount], #pdp-v2-mount');
  }

  function _isV2MultiStepOwnedMount(mount) {
    if (!mount) return false;
    if (mount.getAttribute('data-jrs-multistep-owner') === 'v2') return true;
    return !!mount.querySelector('pdp-multistep-form-v2');
  }

  function _safeClone(value) {
    if (value === null || typeof value === 'undefined') return null;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (e) {
      void e;
      return null;
    }
  }

  function _escapeHTML(input) {
    var str = String(input || '');
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function _escapeAttr(input) {
    return _escapeHTML(input).replace(/`/g, '&#96;');
  }

  JRS.ProductSelector = {
    init: init,
    destroy: destroy,
    getState: getState
  };
})(window.JRS);
